import { Fragment, useState, useEffect } from "react";
import PropTypes from "prop-types";
import ConfirmationModal from "../../../components/ui/popup/ConfirmationModal";
import { getLanguageContent } from "../../../utils/language/languageSelector";
import { FEED_SCHEMA } from "../../../utils/schemas/language/pages/feedSchema";
import {
  updateNewsReaction,
  fetchNewsComments,
  createNewsComment,
  updateCommentReaction,
} from "../../../api/feed/feedAPI";
import CommentModal from "../../../components/ui/popup/common/CommentModal";

function FeedNewCard({
  id,
  title,
  description,
  date,
  image_url,
  source_name = "Read Source",
  source_link,
  likes: initialLikes = 0,
  dislikes: initialDislikes = 0,
  loves: initialLoves = 0,
  userReaction: initialUserReaction = null,
  comments: initialComments = 0,
}) {
  const content = getLanguageContent(FEED_SCHEMA);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);

  // Reaction state
  const [counts, setCounts] = useState({
    likes: initialLikes,
    dislikes: initialDislikes,
    loves: initialLoves,
    comments: initialComments,
  });
  const [userReaction, setUserReaction] = useState(initialUserReaction);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Comments state
  const [commentsList, setCommentsList] = useState([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [isSendingComment, setIsSendingComment] = useState(false);

  // Fetch comments whenever the comment modal is opened
  useEffect(() => {
    if (!isCommentModalOpen || !id) return;

    const loadComments = async () => {
      setIsLoadingComments(true);
      const response = await fetchNewsComments(id);
      if (response.success) {
        // Normalize user_vote / user_reaction (1 -> 1, 2 -> -1)
        const normalizedComments = response.data.map((comment) => {
          const rawVote = comment.user_vote ?? comment.user_reaction;
          const normalizedReaction =
            rawVote === 2 ? -1 : rawVote === 1 ? 1 : null;

          return {
            ...comment,
            user_reaction: normalizedReaction,
            user_vote: rawVote,
          };
        });

        setCommentsList(normalizedComments);
      } else {
        console.error("Failed to load comments:", response.message);
      }
      setIsLoadingComments(false);
    };

    loadComments();
  }, [isCommentModalOpen, id]);

  // Keep counts and userReaction in sync with prop changes
  useEffect(() => {
    setCounts({
      likes: initialLikes,
      dislikes: initialDislikes,
      loves: initialLoves,
      comments: initialComments,
    });
    setUserReaction(initialUserReaction);
  }, [
    initialLikes,
    initialDislikes,
    initialLoves,
    initialComments,
    initialUserReaction,
  ]);

  // Post a new comment to the API
  const handleSendComment = async (text) => {
    if (!text || isSendingComment) return;

    setIsSendingComment(true);

    const response = await createNewsComment(id, text);
    if (response.success) {
      const createdComment = response.data;
      // Prepend newly created comment returned by backend
      setCommentsList((prev) => [createdComment, ...prev]);
      // Increment comment count on the card
      setCounts((prev) => ({ ...prev, comments: prev.comments + 1 }));
    } else {
      console.error("Failed to post comment:", response.message);
    }

    setIsSendingComment(false);
  };

  const handleCommentReaction = async (commentUuid, voteType) => {
    if (!commentUuid) return;

    // Map frontend voteType (-1 for dislike) to backend choice (2 for dislike)
    const backendVoteType = voteType === -1 ? 2 : 1;

    // Snapshot previous state for potential rollback
    let previousComments = [...commentsList];

    // Optimistic UI Update
    setCommentsList((prevComments) =>
      prevComments.map((comment) => {
        if (comment.uuid !== commentUuid) return comment;

        // Handle user_vote or user_reaction normalization
        const currentRawVote = comment.user_vote ?? comment.user_reaction;
        const currentReaction = currentRawVote === 2 ? -1 : currentRawVote;

        let newReaction = voteType;
        let newUpVotes = comment.up_votes || 0;
        let newDownVotes = comment.down_votes || 0;

        // Undo previous vote count
        if (currentReaction === 1) newUpVotes = Math.max(0, newUpVotes - 1);
        if (currentReaction === -1)
          newDownVotes = Math.max(0, newDownVotes - 1);

        // Toggle off if same vote was clicked again, otherwise apply new vote count
        if (currentReaction === voteType) {
          newReaction = null;
        } else {
          if (voteType === 1) newUpVotes += 1;
          if (voteType === -1) newDownVotes += 1;
        }

        return {
          ...comment,
          user_reaction: newReaction,
          user_vote: newReaction === -1 ? 2 : newReaction === 1 ? 1 : null,
          up_votes: newUpVotes,
          down_votes: newDownVotes,
        };
      }),
    );

    try {
      const response = await updateCommentReaction(
        commentUuid,
        backendVoteType,
      );
      if (response.success) {
        // Sync exact server vote counts and state
        setCommentsList((prevComments) =>
          prevComments.map((comment) => {
            if (comment.uuid !== commentUuid) return comment;

            const serverVote = response.data.user_vote;
            const normalizedReaction =
              serverVote === 2 ? -1 : serverVote === 1 ? 1 : null;

            return {
              ...comment,
              up_votes: response.data.up_votes,
              down_votes: response.data.down_votes,
              user_vote: serverVote,
              user_reaction: normalizedReaction,
            };
          }),
        );
      } else {
        // Rollback on server failure
        setCommentsList(previousComments);
      }
    } catch (error) {
      // Rollback on network failure
      setCommentsList(previousComments);
    }
  };

  const cardImage = image_url;

  const displayDate = date
    ? new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "Recent News";

  const handleLinkClick = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleConfirmLeave = () => {
    setIsModalOpen(false);
    window.open(source_link, "_blank", "noopener,noreferrer");
  };

  const handleReactionClick = async (voteType) => {
    if (isSubmitting || !id) return;

    setIsSubmitting(true);

    const previousCounts = { ...counts };
    const previousReaction = userReaction;

    let updatedReaction = voteType;
    let newLikes = counts.likes;
    let newDislikes = counts.dislikes;
    let newLoves = counts.loves;

    if (previousReaction === 1) newLikes = Math.max(0, newLikes - 1);
    if (previousReaction === -1) newDislikes = Math.max(0, newDislikes - 1);
    if (previousReaction === 0) newLoves = Math.max(0, newLoves - 1);

    if (previousReaction === voteType) {
      updatedReaction = null;
    } else {
      if (voteType === 1) newLikes += 1;
      if (voteType === -1) newDislikes += 1;
      if (voteType === 0) newLoves += 1;
    }

    setUserReaction(updatedReaction);
    setCounts({
      ...counts,
      likes: newLikes,
      dislikes: newDislikes,
      loves: newLoves,
    });

    try {
      const response = await updateNewsReaction(id, voteType, "vote");
      if (response.success) {
        setCounts((prev) => ({
          ...prev,
          likes: response.data.likes,
          dislikes: response.data.dislikes,
          loves: response.data.love,
        }));
      } else {
        setUserReaction(previousReaction);
        setCounts(previousCounts);
      }
    } catch (error) {
      setUserReaction(previousReaction);
      setCounts(previousCounts);
    } finally {
      setIsSubmitting(false);
    }
  };

  let domain = "an external site";
  try {
    if (source_link) {
      domain = new URL(source_link).hostname;
    }
  } catch (e) {
    domain = "an external site";
  }

  const hasDescription = Boolean(description && description.trim());

  return (
    <Fragment>
      <div className="feed-new-card-container">
        <div className="card-hero-banner">
          <img src={cardImage} alt={title || "News Image"} loading="lazy" />
        </div>

        <div className="new-card-info">
          <p className="card-info-name">
            {title}{" "}
            {hasDescription && !isExpanded && (
              <button
                type="button"
                className="see-more-toggle-btn-inline"
                onClick={() => setIsExpanded(true)}
              >
                Show More
              </button>
            )}
          </p>

          {hasDescription && isExpanded && (
            <p className="card-info-description fade-in-text">
              {description}{" "}
              <button
                type="button"
                className="see-more-toggle-btn-inline"
                onClick={() => setIsExpanded(false)}
              >
                Show Less
              </button>
            </p>
          )}

          {/*
          For now lets just comment the date, in the future we may want to diplay it
          <p className="card-info-date">{displayDate}</p>
          */}
        </div>

        <div className="new-card-interactions">
          <div className="new-card-interactions-inner">
            <div className="source-link-container">
              {source_link && (
                <a
                  href={source_link}
                  onClick={handleLinkClick}
                  className="cta-button primary"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/icons/news_shortcut.svg`}
                    alt="Source Icon"
                    loading="lazy"
                  />
                  <b style={{ fontWeight: "400" }}> {source_name}</b>
                </a>
              )}
            </div>

            <div className="reactions-comments-container">
              {/* Like Button */}
              <div
                className={`reactions-comments-item ${
                  userReaction === 1 ? "active" : ""
                }`}
                onClick={() => !isSubmitting && handleReactionClick(1)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isSubmitting) {
                    handleReactionClick(1);
                  }
                }}
                aria-label="Like"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/${
                    userReaction === 1
                      ? "like_004789_filled.svg"
                      : "like_004789.svg"
                  }`}
                  alt="Like Icon"
                  loading="lazy"
                />
                {counts.likes > 0 && <span>{counts.likes}</span>}
              </div>

              {/* Dislike Button */}
              <div
                className={`reactions-comments-item ${
                  userReaction === -1 ? "active" : ""
                }`}
                onClick={() => !isSubmitting && handleReactionClick(-1)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isSubmitting) {
                    handleReactionClick(-1);
                  }
                }}
                aria-label="Dislike"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/${
                    userReaction === -1
                      ? "dislike_004789_filled.svg"
                      : "dislike_004789.svg"
                  }`}
                  alt="Dislike Icon"
                  loading="lazy"
                />
                {counts.dislikes > 0 && <span>{counts.dislikes}</span>}
              </div>

              {/* Love Button */}
              <div
                className={`reactions-comments-item ${
                  userReaction === 0 ? "active" : ""
                }`}
                onClick={() => !isSubmitting && handleReactionClick(0)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && !isSubmitting) {
                    handleReactionClick(0);
                  }
                }}
                aria-label="Love"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/${
                    userReaction === 0
                      ? "love_004789_filled.svg"
                      : "love_004789.svg"
                  }`}
                  alt="Love Icon"
                  loading="lazy"
                />
                {counts.loves > 0 && <span>{counts.loves}</span>}
              </div>

              {/* Comment Button */}
              <div
                className="reactions-comments-item"
                role="button"
                tabIndex={0}
                aria-label="Comment"
                onClick={() => setIsCommentModalOpen(true)}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/images/icons/message_004789.svg`}
                  alt="Comment Icon"
                  loading="lazy"
                />
                {counts.comments > 0 && <span>{counts.comments}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmLeave}
        title={content.modals.leaving.title}
        message={content.modals.leaving.warning.replace("{domain}", domain)}
        confirmText={content.modals.leaving.primary_cta}
        cancelText={content.modals.leaving.secondary_cta}
        confirmButtonClass="primary"
        cancelButtonClass="secondary"
      />

      {isCommentModalOpen && (
        <CommentModal
          onClose={() => setIsCommentModalOpen(false)}
          title={title}
          bgImage={cardImage}
          comments={commentsList}
          onSendComment={handleSendComment}
          onCommentReaction={handleCommentReaction}
          isSending={isSendingComment}
          isLoading={isLoadingComments}
        />
      )}
    </Fragment>
  );
}

FeedNewCard.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  date: PropTypes.string,
  image_url: PropTypes.string,
  source_name: PropTypes.string,
  source_link: PropTypes.string,
  likes: PropTypes.number,
  dislikes: PropTypes.number,
  loves: PropTypes.number,
  comments: PropTypes.number,
  userReaction: PropTypes.number,
};

export default FeedNewCard;
